import React, { useEffect, useMemo } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import styled from 'styled-components';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import { GridLayout } from '@Styled/utilities';
import AssetUploadButton from './AssetUploadButton';

export const slugifyAssetKey = (value: string) =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9_-]+/g, '_')
		.replace(/^_+|_+$/g, '');

type Props = {
	open: boolean;
	onClose: () => void;
	onSubmit: (key: string, value: string) => Promise<void>;
	/**
	 * When set (e.g. "subject_icon_"), the key field becomes a friendly
	 * name that is slugified and prefixed on submit.
	 */
	keyPrefix?: string;
	title?: string;
	keyLabel?: string;
};

const AddAssetDialog: React.FC<Props> = ({
	open,
	onClose,
	onSubmit,
	keyPrefix,
	title = 'Add Asset',
	keyLabel = 'Key',
}) => {
	const schema = useMemo(
		() =>
			object({
				key: keyPrefix
					? string().max(80).required()
					: string()
							.matches(
								/^[a-z0-9_-]+$/,
								'Only lowercase letters, numbers, underscores and dashes'
							)
							.max(100)
							.required(),
				value: string().max(2048).required('Upload an image or paste a URL'),
			}),
		[keyPrefix]
	);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { isSubmitting },
	} = useForm<{ key: string; value: string }>({
		resolver: yupResolver(schema),
		defaultValues: { key: '', value: '' },
	});

	useEffect(() => {
		if (open) reset({ key: '', value: '' });
	}, [open]);

	const watchValue = watch('value');
	const watchKey = watch('key');

	const finalKey = keyPrefix
		? `${keyPrefix}${slugifyAssetKey(watchKey ?? '')}`
		: watchKey;

	const submit = async (values: { key: string; value: string }) => {
		const key = keyPrefix
			? `${keyPrefix}${slugifyAssetKey(values.key)}`
			: values.key;
		await onSubmit(key, values.value);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<FieldsLayout>
					<EdFormControl
						control={control}
						required
						name="key"
						label={keyLabel}
						helperText={
							keyPrefix && watchKey ? `Key: ${finalKey}` : undefined
						}
					/>
					<EdFormControl
						control={control}
						required
						name="value"
						label="URL"
						helperText="Upload an image or paste a URL"
					/>
					<div>
						<AssetUploadButton
							onUploaded={(uri) =>
								setValue('value', uri, { shouldValidate: true })
							}
						/>
					</div>
					{watchValue && <PreviewImage src={watchValue} alt="asset preview" />}
				</FieldsLayout>
			</DialogContent>
			<DialogActions>
				<Button color="warning" onClick={onClose}>
					Cancel
				</Button>
				<EdButton
					edcolor="purple"
					disabled={isSubmitting}
					onClick={handleSubmit(submit)}
				>
					Save
				</EdButton>
			</DialogActions>
		</Dialog>
	);
};

export default AddAssetDialog;

const FieldsLayout = styled(GridLayout)`
	grid-template-columns: 1fr;
	gap: 1.5rem;
	padding-top: 0.5rem;
`;

const PreviewImage = styled.img`
	max-width: 100%;
	max-height: 8rem;
	object-fit: contain;
	justify-self: center;
`;
